export type UserMembershipBadge = {
  key: 'club333' | 'allAccess';
  label: string;
  className: string;
  title: string;
};

const truthyMembership = (value: unknown) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  String(value).toLowerCase() === 'true' ||
  String(value).toLowerCase() === 'yes' ||
  String(value).toLowerCase() === 'active';

const collectStrings = (value: unknown): string[] => {
  if (value == null) return [];
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value).toLowerCase()];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectStrings);
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(collectStrings);
  }
  return [];
};

const badgeFallbackFields = [
  'rights',
  'user_rights',
  'permissions',
  'assignMenu',
  'entitlements',
  'badges',
  'logos',
  'user_logos',
  'userLogos',
  'special_logo',
  'specialLogo',
  'specialLogos',
  'special_logos',
  'userLogoDetails',
  'user_logo_details',
  'getuserlogodetails',
  'getUserLogoDetails',
  'getuserLogoDetails',
  'product',
  'product_name',
  'productName',
  'products',
];

const findFallbackUser = (rawUser: Record<string, unknown>, fallbackUsers: unknown) => {
  const userId = rawUser.user_id ?? rawUser.id;
  if (userId == null || !Array.isArray(fallbackUsers)) return undefined;
  const targetId = String(userId);

  return fallbackUsers
    .map((item) => {
      const value = (item || {}) as Record<string, unknown>;
      return (value.user || value) as Record<string, unknown>;
    })
    .find((user) => String(user.user_id ?? user.id) === targetId);
};

const mergeBadgeSource = (rawUser: unknown, fallbackUsers?: unknown) => {
  const user = (rawUser || {}) as Record<string, unknown>;
  const fallbackUser = findFallbackUser(user, fallbackUsers);
  if (!fallbackUser) return user;

  const merged: Record<string, unknown> = { ...fallbackUser, ...user };
  badgeFallbackFields.forEach((field) => {
    if (user[field] == null && fallbackUser[field] != null) {
      merged[field] = fallbackUser[field];
    }
  });
  return merged;
};

const hasRightsId = (user: Record<string, unknown>, rightsId: string) => {
  const directFields = [
    user.rights_id,
    user.right_id,
    user.rightsId,
    user.permission_id,
    user.permissionId,
  ];

  const groupedFields = [
    user.rights,
    user.user_rights,
    user.permissions,
    user.assignMenu,
    user.entitlements,
    user.badges,
    user.logos,
    user.user_logos,
    user.userLogos,
    user.special_logo,
    user.specialLogos,
    user.special_logos,
    user.userLogoDetails,
    user.user_logo_details,
    user.getuserlogodetails,
    user.getUserLogoDetails,
    user.getuserLogoDetails,
  ];

  return [...directFields, ...groupedFields]
    .flatMap(collectStrings)
    .some((value) => value === rightsId);
};

const membershipWords = (user: Record<string, unknown>) =>
  [
    user.membership,
    user.membership_name,
    user.membershipName,
    user.plan,
    user.plan_name,
    user.planName,
    user.subscription,
    user.subscription_name,
    user.subscriptionName,
    user.entitlement,
    user.entitlement_name,
    user.entitlementName,
    user.entitlements,
    user.permissions,
    user.roles,
    user.badges,
    user.logos,
    user.user_logos,
    user.userLogos,
    user.special_logo,
    user.specialLogo,
    user.specialLogos,
    user.special_logos,
    user.special_logo_name,
    user.specialLogoName,
    user.userLogoDetails,
    user.user_logo_details,
    user.getuserlogodetails,
    user.getUserLogoDetails,
    user.getuserLogoDetails,
    user.product,
    user.product_name,
    user.productName,
    user.products,
    user.access_level,
    user.accessLevel,
  ].flatMap(collectStrings);

export const getUserMembershipBadges = (
  rawUser: unknown,
  fallbackUsers?: unknown
): UserMembershipBadge[] => {
  const user = mergeBadgeSource(rawUser, fallbackUsers);
  const words = membershipWords(user);
  const hasClub333 =
    truthyMembership(user.club333) ||
    truthyMembership(user.club_333) ||
    truthyMembership(user.isClub333) ||
    truthyMembership(user.is_club333) ||
    truthyMembership(user.club333_member) ||
    hasRightsId(user, '14') ||
    words.some((value) => /club\s*333|club333|special_logo_6\.png/.test(value));

  const hasAllAccess =
    truthyMembership(user.all_access) ||
    truthyMembership(user.allAccess) ||
    truthyMembership(user.has_all_access) ||
    truthyMembership(user.hasAllAccess) ||
    truthyMembership(user.is_all_access) ||
    truthyMembership(user.isAllAccess) ||
    hasRightsId(user, '13') ||
    words.some((value) => /all[-_\s]*access|loungeland|lounge\s*land/.test(value));

  return [
    ...(hasClub333
      ? [
          {
            key: 'club333' as const,
            label: 'Club 333',
            className: 'mw-comment-badge--club333',
            title: 'Club 333 member',
          },
        ]
      : []),
    ...(hasAllAccess
      ? [
          {
            key: 'allAccess' as const,
            label: 'ALL-ACCESS',
            className: 'mw-comment-badge--all-access',
            title: 'ALL-ACCESS member',
          },
        ]
      : []),
  ];
};
